import { Tag } from "@carbon/react";
import { TableColumns } from "@flaminem/ui-components";
import { capitalize } from "@flaminem/utils";

import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import { usePermissions } from "src/authentication/usePermissions.ts";
import { CaseTypeTag } from "src/components/CaseTypeTag";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.tsx";
import { BlacklistElementOrigin as ApiBlacklistElementOrigin } from "src/graphql/anti-fraud/__generated__/graphql";
import { useGetBlacklistElementOriginLabel } from "src/hooks/blacklist";
import { useT } from "src/internationalization";
import { mapToBlacklistElementOrigin } from "src/mappers/blacklist.mappers";
import { BlacklistElementRecord, useListMetaData } from "src/pages/Blacklists/blacklist.mapping.ts";
import { useListSchema } from "src/pages/Blacklists/useListSchema.ts";

import { BlacklistActions } from "./BlacklistActions";

type UseGetBlacklistsTableColumnsProps = {
  onStatusChanged: () => void;
  onElementUpdated: () => void;
};

export const useGetBlacklistsTableColumns = ({
  onStatusChanged,
  onElementUpdated,
}: UseGetBlacklistsTableColumnsProps) => {
  const { listSchema } = useListSchema();
  const { hasPartialPermission } = usePermissions();
  const t = useT();
  const listMetaData = useListMetaData();
  const getBlacklistElementOriginLabel = useGetBlacklistElementOriginLabel();
  const { caseTypeDefinitions } = useDynamicViewSchema();

  const columns: TableColumns<BlacklistElementRecord> = {
    ...(listSchema?.properties ?? []).reduce((obj, prop) => {
      obj[prop.name] = {
        label: capitalize(prop.name),
        flexGrow: 1,
        flexShrink: 1,
        width: 1,
        className: "text-sm",
        render: (row) => row[prop.name],
      };
      return obj;
    }, {}),
    ...listMetaData
      .filter(({ header }) => !!header)
      .reduce((obj, prop) => {
        obj[prop.name] = {
          label: capitalize(prop.header ?? prop.name),
          flexGrow: 1,
          flexShrink: 1,
          width: 1,
          className: "text-sm",
          render: (row) => {
            switch (prop.name) {
              case "status":
                return (
                  <Tag type={row[prop.name] ? "green" : "red"}>
                    {row[prop.name]
                      ? t("page.blacklists.table.status.active")
                      : t("page.blacklists.table.status.inactive")}
                  </Tag>
                );
              case "origin":
                return row[prop.name]
                  ? getBlacklistElementOriginLabel(
                      mapToBlacklistElementOrigin(row[prop.name] as ApiBlacklistElementOrigin)
                    )
                  : "";
              case "sourceCaseType.name": {
                const caseTypeCode = row[prop.name];
                return (
                  <CaseTypeTag
                    caseTypeCode={caseTypeCode}
                    caseTypeDefinitions={caseTypeDefinitions}
                    fallbackType="magenta"
                  />
                );
              }
              default:
                return row[prop.name];
            }
          },
        };
        return obj;
      }, {}),
    ...(hasPartialPermission(ScopeResource.BLACKLIST_ELEMENT, Scope.DISABLE, Scope.EDIT) && {
      actions: {
        label: "",
        width: 100,
        render: (row) => (
          <BlacklistActions
            blacklistElementRecord={row}
            onStatusChange={onStatusChanged}
            onElementUpdated={onElementUpdated}
          />
        ),
      },
    }),
  };

  return columns;
};
